<template>
  <section>
    <div v-if="hasSubmit">
      <v-btn @click="getReport">Get Report</v-btn>
    </div>
    <div v-show="!isReportLoaded" class="center-progress">
      <v-progress-circular :size="50" color="primary" indeterminate />
      <p class="mt-5 text-primary">Loading Report...</p>
    </div>
    <div v-show="isReportLoaded" id="reportContainer" ref="reportContainer">
      <ReportBar :reportName="reportName" />
      <div id="report" ref="report" class="mt-n2" />
    </div>
  </section>
</template>

<script>
import { refreshDefaultData as refreshDefaultStrategyEmbed } from '@/modules/reports/_factories/embedReports/defaultEmbed.js';
import { refreshDefaultData as refreshRealTimeStrategyEmbed } from '@/modules/reports/_factories/embedReports/realTimeEmbed.js';
import api from '../_api';
import powerBiReportfactory from '../_factories/powerBiReportFactory.js';
import reportGuids from '../_factories/reportGuids.js';
import { mapGetters } from 'vuex';

import ReportBar from '@/Core.Service.Domain/ViewSupport/Reports/Components/ReportBar.vue';

const REAL_TIME_PATTERN = /real\s*-?\s*time/i;

export default {
  name: 'PowerBIReport',
  props: {
    reportName: { type: String, required: true },
    siteId: { type: String, required: false },
    hasSubmit: { type: Boolean, default: false },
    controlFriendlyName: { type: String, default: '' },
  },

  components: {
    ReportBar,
  },

  data() {
    return {
      embedConfiguration: null,
      refreshRate: 30000,
      timeoutId: null,
      isDashboard: false,
      intervalId: null,
      isReportLoaded: false,
    };
  },

  beforeUnmount() {
    clearInterval(this.intervalId);
    clearInterval(window.refreshTokenInterval);
    this.$route.meta.isRealTimeReport = false;
  },

  watch: {
    reportName: function (newVal) {
      if (!this.hasSubmit && newVal && newVal !== '') {
        this.getReport();
      }
    },
    isRealTimeReport: {
      handler(isRealTimeReport) {
        this.$route.meta.isRealTimeReport = isRealTimeReport;

        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }

        if (!isRealTimeReport || this.isDashboard) {
          return;
        }

        this.intervalId = setInterval(this.refreshReportData, this.refreshRate);
      },
      immediate: true,
    },
  },

  computed: {
    ...mapGetters({
      reportInfo: 'ViewSupportStore/ReportsStore/getReportInfo',
    }),

    // A report counts as real time when the embed configuration returned by the
    // API says so, when the selected report card says so, or when the route
    // itself points at a real time report.
    isRealTimeReport() {
      return [
        this.embedConfiguration?.reportType,
        this.reportInfo?.reportType,
        this.$route.path,
      ].some((value) => REAL_TIME_PATTERN.test(value ?? ''));
    },
  },

  created() {
    this.getReport();
  },

  methods: {
    // Every real time report is refreshed the same way (report.refresh()), but
    // each embed strategy keeps its own report instance, so the call has to go
    // to the strategy that actually rendered this report: only 'Realtime
    // Report' goes through the real time one, while the remaining real time
    // reports (e.g. PJM Top 5 Peak Loads) are embedded by the default one.
    refreshReportData: function () {
      const refresh =
        this.reportName === reportGuids.RealTime
          ? refreshRealTimeStrategyEmbed
          : refreshDefaultStrategyEmbed;

      return refresh().catch((error) => {
        console.error('Error refreshing report data:', error);
      });
    },

    getReport: async function () {
      const embedConfiguration = await api.getPowerBiEmbedConfiguration(
        this.reportName,
        this.siteId
      );

      // Set before embedConfiguration so the isRealTimeReport watcher already
      // sees the right value when it decides whether to poll for refreshes.
      this.isDashboard = embedConfiguration.type === 'dashboard';

      this.embedConfiguration = embedConfiguration;

      if (this.controlFriendlyName) {
        embedConfiguration.reportParameters.SensorName =
          this.controlFriendlyName;
      }

      const container = document.getElementById('report');
      container.addEventListener('loaded', () => {
        this.isReportLoaded = true;
      });

      powerBiReportfactory.getPowerBiReportStrategy(
        this.reportName,
        container,
        embedConfiguration
      );
    },
  },
};
</script>
<style lang="scss">
#report {
  position: relative;
  height: 98%;
  width: auto;
}

#report iframe {
  border: none;
}

#reportContainer {
  height: 85vh !important;
  position: relative;
}

.center-progress {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 85vh;
}
</style>
